import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import type { Lens } from '~/types/leLens';

import CustomLensView from './customView/CustomLensView';
import CustomViewCodeDiff from './customView/CustomViewCodeDiff';
import type { LensProposal } from './lensProposal';
import { customViewPresentationChanged, reviewDefinitionChanges } from './reviewLensCandidate';

/** Full structural review never calls a card-data, query, or dry-run endpoint. */
export default function LensStructuralProposalPreview({
  lens,
  definition,
  reviewKind = 'proposal',
}: {
  lens: Lens;
  definition: NonNullable<LensProposal['definition']>;
  reviewKind?: 'proposal' | 'draft';
}) {
  const changes = reviewDefinitionChanges(lens, definition);
  const before = lens.spec.customView;
  const after = definition.spec.customView;
  const customChanged = customViewPresentationChanged(before, after);
  return (
    <Stack spacing={1.5} data-testid="lens-structural-proposal-preview">
      {changes.map((change) => (
        <Box key={change.field}>
          <Typography variant="subtitle2">{change.label}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {change.before} → {change.after}
          </Typography>
        </Box>
      ))}
      {customChanged && (
        <Stack spacing={1}>
          {after ? (
            <>
              <Typography variant="subtitle2">Proposed custom view</Typography>
              <CustomLensView
                view={{ ...after, height: Math.min(after.height ?? 400, 500) }}
                lens={{ ...lens, ...definition }}
                cards={[]}
                filters={{}}
                period={{ summary: 'Proposal preview' }}
                previewOnly
              />
            </>
          ) : (
            <Typography variant="body2">Custom view removed</Typography>
          )}
          {(['html', 'css', 'javascript'] as const).map((part) =>
            (before?.[part] ?? '') !== (after?.[part] ?? '') ? (
              <CustomViewCodeDiff key={part} part={part} before={before?.[part] ?? ''} after={after?.[part] ?? ''} />
            ) : null,
          )}
        </Stack>
      )}
      <Typography variant="body2" color="text.secondary">
        {reviewKind === 'proposal'
          ? 'These are proposed changes. The saved Lens stays the same until its owner approves them.'
          : 'Review these draft changes before saving them to the Lens.'}
      </Typography>
    </Stack>
  );
}
